import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Table, message } from 'antd';
import TableFooter from './Footer';
import FilterList from './FilterList';
import { deserializeParams } from '@services/transform';
import { getErrors } from '@utils/error';

function ListTable(props) {
  const {
    columns,
    fetchAPI,
    pageSizes,
    defaultFilter,
    defaultPageSize,
    dataId,
    filterDefinitions,
    dataFields,
    onCreate,
    setCache,
    extraFlag,
    rowClassName
  } = props;
  const [pagination, setPagination] = useState({
    currentPage: 1
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [sorter, setSorter] = useState({});
  const [filter, setFilter] = useState(defaultFilter);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [offset, setOffset] = useState(0);

  const fetch = async () => {
    setLoading(true);
    try {
      const {
        result: { [dataFields]: resData, pagination: newPagination }
      } = await fetchAPI({
        offset,
        limit: pageSize,
        ...sorter,
        ...deserializeParams(filter)
      });

      setData(resData || []);
      if (setCache) {
        setCache(resData || []);
      }
      setPagination(newPagination);
    } catch (err) {
      const error = getErrors(err);
      message.error(error.message || 'Sorry, error in processing your request');
    }

    setLoading(false);
  };

  useEffect(() => {
    fetch();
  }, [filter, offset, pageSize, sorter, extraFlag]);

  const handlePageSizeChange = newPageSize => {
    if (pageSize === newPageSize) {
      return;
    }

    setPageSize(newPageSize);
    setOffset(0);
    const newPagination = {
      ...pagination,
      currentPage: 1
    };
    setPagination(newPagination);
  };

  const handleTableChange = (
    disabledPagination,
    disabledFilters,
    tableSort
  ) => {
    const newSort = {
      orderBy: tableSort.field,
      orderDirection: tableSort.order === 'descend' ? 'desc' : 'asc'
    };

    const { currentPage } = pagination;
    let newOffset = (currentPage - 1) * pageSize;
    const newPagination = {
      ...pagination
    };

    if (
      newSort.orderBy !== sorter.orderBy ||
      newSort.orderDirection !== sorter.orderDirection
    ) {
      newOffset = 0;
      newPagination.currentPage = 1;
    }

    setPagination(newPagination);
    setSorter(newSort);
    setOffset(newOffset);
  };

  const handlePageChange = newPage => {
    const newOffset = (newPage - 1) * pageSize;

    const newPagination = {
      ...pagination,
      currentPage: newPage
    };
    setOffset(newOffset);
    setPagination(newPagination);
  };

  return (
    <div className="properties-view">
      <Table
        columns={columns}
        bordered
        rowKey={dataId}
        title={() => (
          <FilterList
            columns={filterDefinitions}
            onChange={(key, val) => {
              setFilter({
                ...filter,
                [key]: val
              });
            }}
            value={filter}
            filter={filter}
            onClear={() => setFilter(defaultFilter)}
            onReload={() => fetch()}
            onCreate={onCreate}
          />
        )}
        dataSource={data}
        loading={loading}
        onChange={handleTableChange}
        rowClassName={rowClassName}
        pagination={false}
        footer={() => (
          <TableFooter
            changeNoOfItemsToDisplay={handlePageSizeChange}
            noOfItemsToDisplay={pageSize}
            currentItemsDisplayed={data.length}
            handlePageChange={handlePageChange}
            pageSizes={pageSizes}
            {...pagination}
          />
        )}
        scroll={{ x: 400 }}
      />
    </div>
  );
}

ListTable.propTypes = {
  columns: PropTypes.array.isRequired,
  fetchAPI: PropTypes.func.isRequired,
  pageSizes: PropTypes.array,
  defaultFilter: PropTypes.object,
  defaultPageSize: PropTypes.number,
  dataId: PropTypes.string,
  filterDefinitions: PropTypes.array,
  dataFields: PropTypes.string,
  onCreate: PropTypes.func,
  setCache: PropTypes.func,
  extraFlag: PropTypes.any,
  rowClassName: PropTypes.func
};

export default ListTable;
